// src/pages/RentalListingsReport.js
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchRentalListingsByAgency } from '../services/reportService';
import { formatMoney, generateRentalListingsPdf } from '../utils/pdfUtils';
import { formatDate } from '../utils/dateUtils';
import { AuthContext } from '../contexts/AuthContext';
import PageWrapper from '../components/PageWrapper';
import {
  ChevronLeftIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 20;
const MAX_PDF_ROWS = 1000;

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Format an array of renter objects to a readable string */
const formatTenants = (renters = []) => {
  if (!renters || renters.length === 0) return '—';
  return renters
    .map((r) => [r.person?.given || r.given, r.person?.surname || r.surname].filter(Boolean).join(' '))
    .join(', ');
};

// ─── component ───────────────────────────────────────────────────────────────

const RentalListingsReport = () => {
  const { user, agencyCountry } = useContext(AuthContext);
  const navigate = useNavigate();

  const formatDateCtx = (d) =>
    formatDate(d, { userPreference: user?.dateFormatPreference, agencyCountry });

  // Filter state
  const [overdueOnly, setOverdueOnly]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Data state
  const [listings, setListings] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [hasMore, setHasMore]   = useState(true);

  // UI state
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore]       = useState(false);
  const [error, setError]                   = useState(null);
  const [pdfLoading, setPdfLoading]         = useState(false);
  const [pdfError, setPdfError]             = useState(null);

  const agencyId = sessionStorage.getItem('agencyId');

  const sentinelRef  = useRef(null);
  const isFetchingRef = useRef(false);

  // ── data fetching ──────────────────────────────────────────────────────────

  const fetchPage = useCallback(async (pageNum, isFirstPage) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      if (isFirstPage) setInitialLoading(true);
      else setLoadingMore(true);
      setError(null);

      const data = await fetchRentalListingsByAgency({
        agencyId,
        page: pageNum,
        pageSize: PAGE_SIZE,
        overdueOnly,
        status: statusFilter === 'all' ? '' : statusFilter,
      });

      const incoming = data.listings || [];
      setTotal(data.total || 0);
      setListings((prev) => (isFirstPage ? incoming : [...prev, ...incoming]));
      setHasMore(pageNum < (data.total_pages || 1));
    } catch (err) {
      setError(err.message || 'Failed to load report data');
      // Stop infinite scroll on error — prevent the IntersectionObserver from
      // firing more requests while the error banner is visible.
      setHasMore(false);
    } finally {
      if (isFirstPage) setInitialLoading(false);
      else setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [overdueOnly, statusFilter, agencyId]);

  // Reset and reload when filters change
  useEffect(() => {
    setListings([]);
    setPage(1);
    setHasMore(true);
    fetchPage(1, true);
  }, [overdueOnly, statusFilter]);

  // Load next page when page advances (not initial)
  useEffect(() => {
    if (page === 1) return;
    fetchPage(page, false);
  }, [page]);

  // ── IntersectionObserver ───────────────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !initialLoading) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, initialLoading]);

  // ── PDF download ───────────────────────────────────────────────────────────

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      let allListings = [];
      let currentPage = 1;
      let totalPages  = 1;

      do {
        const data = await fetchRentalListingsByAgency({
          agencyId,
          page: currentPage,
          pageSize: 100,
          overdueOnly,
          status: statusFilter === 'all' ? '' : statusFilter,
        });
        allListings = [...allListings, ...(data.listings || [])];
        totalPages  = data.total_pages || 1;

        if (allListings.length >= MAX_PDF_ROWS) {
          allListings = allListings.slice(0, MAX_PDF_ROWS);
          setPdfError(
            `PDF capped at ${MAX_PDF_ROWS.toLocaleString()} rows. ` +
            `Use filters to narrow results if you need the full dataset.`
          );
          break;
        }
        currentPage += 1;
      } while (currentPage <= totalPages);

      const agencyName = sessionStorage.getItem('agency_name') || '';

      // Convert listing shape to the format generateRentalListingsPdf expects
      const tenancyShapedData = allListings.map((listing) => ({
        id: listing.id,
        status: listing.current_tenancy?.status || 'vacant',
        rental_listing: {
          id: listing.id,
          property_id: listing.property_id || listing.room?.property_id,
          property_name: listing.property?.name || '—',
          property_address: listing.property?.address || '',
          room_number: listing.room?.room_number,
        },
        rental_agreement: listing.current_tenancy?.rental_agreement
          ? {
              renters: listing.current_tenancy.rental_agreement.renters || [],
              payment_type: listing.current_tenancy.rental_agreement.payment_type,
              rent_frequency: listing.current_tenancy.rental_agreement.rent_frequency,
            }
          : null,
        next_rent_due_date: listing.current_tenancy?.next_rent_due_date || null,
        tenancy_ledger: listing.current_tenancy?.tenancy_ledger || null,
      }));

      generateRentalListingsPdf(tenancyShapedData, { overdueOnly, statusFilter, agencyName });
    } catch (err) {
      setPdfError(err.message || 'Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  // ── row navigation ─────────────────────────────────────────────────────────

  const handleRowClick = (listing) => {
    // Resolve the parent property ID (direct for single_rental, via room for multi_rental)
    const propertyId = listing.property_id || listing.room?.property_id;
    if (!propertyId) return;
    navigate(`/properties/${propertyId}?listingId=${listing.id}`);
  };

  // ── render helpers ─────────────────────────────────────────────────────────

  const renderBalance = (tenancyLedger) => {
    if (!tenancyLedger) return <span className="text-gray-400">—</span>;
    const { balance } = tenancyLedger;
    const isNegative = balance?.amount < 0;
    return (
      <span className={isNegative ? 'font-semibold text-red-600' : 'text-gray-700'}>
        {formatMoney(balance)}
      </span>
    );
  };

  const renderStatusBadge = (status) => {
    if (!status || status === 'vacant') {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 capitalize">
          Vacant
        </span>
      );
    }
    const styles =
      status === 'active'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'inactive' || status === 'terminated'
        ? 'bg-gray-100 text-gray-600'
        : status === 'expired'
        ? 'bg-orange-100 text-orange-700'
        : 'bg-amber-100 text-amber-700'; // pending / available / onHold
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${styles}`}>
        {status}
      </span>
    );
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse">
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </td>
      ))}
    </tr>
  );

  // ── main render ────────────────────────────────────────────────────────────
  return (
    <PageWrapper>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center text-sm text-gray-500">
        <Link to="/reports" className="flex items-center hover:text-gray-700 transition-colors">
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Reports
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900 font-medium">Rental Listings</span>
      </div>

      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rental Listings Report</h1>
          <p className="mt-1 text-sm text-gray-500">
            {initialLoading
              ? 'Loading…'
              : `${total.toLocaleString()} listing${total !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap">
          {/* Status filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="available">Available</option>
              <option value="expired">Expired</option>
              <option value="terminated">Terminated</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Overdue toggle */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={overdueOnly}
                onChange={(e) => setOverdueOnly(e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-red-500 transition-colors" />
              <div className="absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
            </div>
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {overdueOnly
                ? <span className="text-red-600 font-semibold">Overdue only</span>
                : 'Show overdue only'}
            </span>
          </label>

          <div className="hidden sm:block h-6 w-px bg-gray-200" />

          {/* PDF */}
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading || initialLoading || total === 0}
            className={`
              inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium
              transition-colors border shadow-sm
              ${pdfLoading || initialLoading || total === 0
                ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300'}
            `}
          >
            {pdfLoading ? (
              <>
                <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Generating…
              </>
            ) : (
              <>
                <DocumentArrowDownIcon className="h-4 w-4" />
                Download PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Banners */}
      {pdfError && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-500" />
          <div className="flex-1">{pdfError}</div>
          <button onClick={() => setPdfError(null)} className="text-amber-500 hover:text-amber-700 font-bold leading-none">✕</button>
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">{error}</div>
          <button
            onClick={() => {
              setError(null);
              setListings([]);
              setPage(1);
              setHasMore(true);
              fetchPage(1, true);
            }}
            className="flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Property</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Room</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tenant(s)</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Agreement Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Next Rent Due</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 bg-white">
              {initialLoading && Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}

              {!initialLoading && listings.map((listing) => {
                const tenancy     = listing.current_tenancy;
                const agreement   = tenancy?.rental_agreement;
                const renters     = agreement?.renters || [];
                const paymentType = agreement?.payment_type || '';
                const rentFreq    = agreement?.rent_frequency || '';
                const nextRentDue = tenancy?.next_rent_due_date || null;
                const status      = tenancy?.status || null;

                // Property info — direct (single_rental) or via room (multi_rental)
                const propertyName    = listing.property?.name || '—';
                const propertyAddress = listing.property?.address || '';
                const roomNumber      = listing.room?.room_number;

                const agreementType = [
                  paymentType ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1) : '',
                  rentFreq    ? rentFreq.charAt(0).toUpperCase()    + rentFreq.slice(1)    : '',
                ].filter(Boolean).join(' / ');

                return (
                  <tr
                    key={listing.id}
                    onClick={() => handleRowClick(listing)}
                    className="hover:bg-emerald-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{propertyName}</div>
                      {propertyAddress && (
                        <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{propertyAddress}</div>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-600">
                      {roomNumber != null
                        ? `Room ${roomNumber}`
                        : <span className="text-gray-400 italic text-xs">Whole property</span>}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {tenancy ? formatTenants(renters) : <span className="text-gray-400 italic text-xs">—</span>}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {agreementType ? (
                        <span className="inline-flex items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            paymentType === 'prepay'
                              ? 'bg-blue-100 text-blue-700'
                              : paymentType === 'postpay'
                              ? 'bg-violet-100 text-violet-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {paymentType ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1) : '—'}
                          </span>
                          {rentFreq && <span className="text-xs text-gray-500 capitalize">{rentFreq}</span>}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {nextRentDue
                        ? <span className="font-medium text-gray-800">{formatDateCtx(nextRentDue)}</span>
                        : <span className="text-gray-400 italic text-xs">None scheduled</span>}
                    </td>

                    <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                      {renderBalance(tenancy?.tenancy_ledger)}
                    </td>

                    <td className="px-6 py-4">
                      {renderStatusBadge(status)}
                    </td>
                  </tr>
                );
              })}

              {!initialLoading && listings.length === 0 && !error && (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-gray-400">
                    <p className="text-base font-medium">
                      {overdueOnly
                        ? 'No overdue listings found.'
                        : statusFilter === 'all'
                        ? 'No rental listings found.'
                        : `No ${statusFilter} rental listings found.`}
                    </p>
                    {overdueOnly && (
                      <p className="text-sm mt-1">All tenants are up to date — great news!</p>
                    )}
                  </td>
                </tr>
              )}

              {loadingMore && (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center">
                    <svg className="inline h-5 w-5 animate-spin text-emerald-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!initialLoading && listings.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400 flex justify-between">
            <span>Showing {listings.length.toLocaleString()} of {total.toLocaleString()} listing{total !== 1 ? 's' : ''}</span>
            {!hasMore && <span>All listings loaded</span>}
          </div>
        )}
      </div>

      {/* Sentinel for IntersectionObserver */}
      <div ref={sentinelRef} className="h-px" aria-hidden="true" />
    </PageWrapper>
  );
};

export default RentalListingsReport;
